// Renders a PDF's pages stacked vertically, each with an overlay layer for fields. Pages render when they come
// near the viewport, so a 100-page document doesn't paint everything at once.
import { useEffect, useRef, useState, type ReactNode } from "react";
import type { PDFDocumentProxy } from "pdfjs-dist";
import { loadPdf, renderPage } from "../lib/pdf";
import type { PageInfo } from "../lib/types";
import { ErrorNote, Spinner } from "./ui";

interface Props {
  url: string;
  pages: PageInfo[];
  /** Absolutely positioned children over page i (fractions of the page map to percentages). */
  overlay?: (page: number, size: { width: number; height: number }) => ReactNode;
  onPageClick?: (page: number, fx: number, fy: number) => void;
  maxWidth?: number;
  className?: string;
}

export function PdfPages({ url, pages, overlay, onPageClick, maxWidth = 860, className }: Props) {
  const [doc, setDoc] = useState<PDFDocumentProxy | null>(null);
  const [error, setError] = useState<string | null>(null);
  const wrap = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let loaded: { destroy: () => Promise<void> } | null = null;
    setDoc(null);
    setError(null);
    loadPdf(url)
      .then((d) => {
        loaded = d;
        if (!cancelled) setDoc(d.doc);
        else void d.destroy();
      })
      .catch((e) => !cancelled && setError(`The document couldn't be displayed (${(e as Error).message}).`));
    return () => {
      cancelled = true;
      void loaded?.destroy();
    };
    // The presigned URL changes on every load; the document only changes when its path does.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url.split("?")[0]]);

  useEffect(() => {
    const el = wrap.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setWidth(Math.min(maxWidth, Math.floor(el.clientWidth))));
    ro.observe(el);
    return () => ro.disconnect();
  }, [maxWidth]);

  return (
    <div ref={wrap} className={className}>
      {error && <ErrorNote>{error}</ErrorNote>}
      {!doc && !error && <Spinner label="Loading document…" />}
      {doc && width > 0 && (
        <div className="flex flex-col items-center gap-4">
          {pages.map((p, i) => (
            <Page key={i} doc={doc} index={i} width={width} aspect={p.h / p.w} overlay={overlay} onPageClick={onPageClick} />
          ))}
        </div>
      )}
    </div>
  );
}

function Page({ doc, index, width, aspect, overlay, onPageClick }: { doc: PDFDocumentProxy; index: number; width: number; aspect: number; overlay?: Props["overlay"]; onPageClick?: Props["onPageClick"] }) {
  const box = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const [visible, setVisible] = useState(false);
  const height = Math.round(width * aspect);

  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const io = new IntersectionObserver((e) => e.some((x) => x.isIntersecting) && setVisible(true), { rootMargin: "800px 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  useEffect(() => {
    if (!visible || !canvas.current) return;
    let stale = false;
    renderPage(doc, index, canvas.current, width).catch((e) => !stale && console.warn("render failed", index, e));
    return () => {
      stale = true;
    };
  }, [visible, doc, index, width]);

  return (
    <div
      ref={box}
      data-page={index}
      className="relative bg-white shadow-sm ring-1 ring-black/10 select-none"
      style={{ width, height }}
      onClick={(e) => {
        if (!onPageClick || e.target !== e.currentTarget.querySelector("[data-hit]")) return;
        const r = e.currentTarget.getBoundingClientRect();
        onPageClick(index, (e.clientX - r.left) / r.width, (e.clientY - r.top) / r.height);
      }}
    >
      <canvas ref={canvas} className="absolute inset-0" aria-label={`Page ${index + 1}`} />
      <div data-hit className="absolute inset-0" />
      {overlay?.(index, { width, height })}
      <div className="absolute -left-10 top-1 text-xs text-muted tabular hidden lg:block" aria-hidden>
        {index + 1}
      </div>
    </div>
  );
}
